import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { tw } from 'utils/classnames'
import { Spinner } from '../spinner/spinner'

/**
 * Unofficial variants while colors are in-progress by the design system team
 */
const customVariants = {
  'primary-green-bright':
    'text-cf-white bg-[#3AB561] hover:text-cf-grey-1000 border border-[#3AB561] disabled:border-cf-grey-300',
  'primary-green':
    'text-cf-white bg-[#0F7B59] hover:bg-[#0B5A41] border border-[#0F7B59] disabled:border-cf-grey-300',
  'secondary-grey': 'border-2 border-cf-grey-600 text-cf-grey-1000 hover:bg-cf-grey-50',
  'tertiary-grey':
    'text-cf-grey-1000 bg-cf-grey-100 hover:bg-cf-grey-200 active:bg-cf-grey-300 disabled:text-cf-grey-400',
  'yellow-cta':
    'bg-[#f6ff00] hover:bg-[#c6d14f] transition-all w-full mx-auto block rounded-[2px] uppercase shadow-[0px_5px_12px_1px_rgba(135,135,135,0.3)] leading-[1] text-center text-cf-black font-normal py-2.5',
  'green-pill':
    'text-secondary-500 border border-secondary-500 hover:bg-secondary-500 hover:text-cf-white transition-all rounded-[2px] font-normal p-[5px] text-xs',
  destructive:
    'text-cf-white bg-cf-red-900 hover:bg-cf-red-1000 active:bg-cf-red-800 border border-cf-red-900 disabled:border-cf-grey-300',
}

const buttonVariants = cva(
  'font-semibold inline-flex items-center justify-center whitespace-nowrap rounded-2 transition-colors [&_svg]:pointer-events-none [&_svg]:shrink-0 disabled:bg-cf-grey-300 disabled:text-cf-grey-500 disabled:border-cf-grey-300 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'text-cf-white bg-cf-purple-900 hover:bg-cf-purple-1000 active:bg-cf-purple-800',
        outline:
          'text-cf-purple-900 hover:bg-cf-purple-50 border-2 border-cf-purple-900 disabled:border-transparent active:bg-cf-purple-50',
        secondary:
          'text-cf-purple-900 bg-cf-grey-0 hover:bg-cf-purple-100 border-2 border-cf-purple-900 disabled:border-transparent active:bg-cf-purple-50',
        tertiary:
          'text-cf-purple-900 bg-cf-purple-100 hover:bg-cf-purple-200 active:bg-cf-purple-50',
        ghost: 'text-cf-purple-900 hover:bg-cf-grey-100 active:bg-cf-grey-200',
        'primary-inverse':
          'text-cf-purple-900 bg-cf-grey-0 hover:bg-cf-transparent-grey-0-88 active:bg-cf-transparent-grey-0-72',
        ...customVariants,
      },
      size: {
        regular: 'text-14/4 [&_svg]:h-6 [&_svg]:w-6 min-h-9',
        small: 'text-12/[14px] [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem] min-h-[1.625rem]',
        large: 'text-16/[18px] [&_svg]:h-7 [&_svg]:w-7 min-h-12',
        custom: '',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'regular',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  fullWidth?: boolean
  loading?: boolean
  loadingText?: string
  textClassName?: string
}

// oxlint-disable-next-line complexity
const Button = ({
  ref,
  className,
  variant,
  size,
  fullWidth,
  asChild = false,
  iconLeft,
  iconRight,
  children,
  loading = false,
  loadingText = 'Loading...',
  textClassName,
  ...props
}: ButtonProps & {
  ref?: React.Ref<HTMLButtonElement | null>
}) => {
  const Comp = asChild ? Slot : 'button'
  const hasOnlyOneIcon = !children && (iconLeft || iconRight) && !(iconLeft && iconRight)
  const paddingClasses = tw({
    'px-[0.625rem]':
      (hasOnlyOneIcon && size === 'large') ||
      (!hasOnlyOneIcon && (size === 'regular' || size === undefined)),
    'px-1': hasOnlyOneIcon && size === 'small',
    'px-[0.375rem]': hasOnlyOneIcon && (size === 'regular' || size === undefined),
    'px-[0.875rem]': !hasOnlyOneIcon && size === 'large',
    'px-[0.5rem]': !hasOnlyOneIcon && size === 'small',
  })

  const buttonClassName = tw(
    buttonVariants({ variant, size, fullWidth, className }),
    hasOnlyOneIcon && 'aspect-square',
    paddingClasses,
    loading && 'relative pointer-events-none cursor-not-allowed',
  )

  const textClassNameComputed = tw(
    {
      'mx-2': size === 'large' && !hasOnlyOneIcon,
      'mx-1': size === 'small' && !hasOnlyOneIcon,
      'mx-1.5': (size === 'regular' || size === undefined) && !hasOnlyOneIcon,
    },
    textClassName,
  )

  const spinnerClassName = tw({
    'h-4 w-4': size === 'small',
    'h-5 w-5': size === 'regular' || size === undefined,
    'h-7 w-7': size === 'large',
  })

  const elementOpacity = loading ? 'opacity-0' : ''

  const accessibilityProps = loading
    ? {
        'aria-busy': true,
        'aria-disabled': true,
        'aria-label': loadingText,
      }
    : {
        'aria-busy': false,
      }

  if (asChild) {
    return (
      <Comp
        className={buttonClassName}
        ref={ref}
        disabled={props.disabled || loading}
        {...accessibilityProps}
        {...props}
      >
        {loading ? (
          <div className="relative">
            <div className={elementOpacity}>{children}</div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className={spinnerClassName} aria-hidden="true" />
              <span className="sr-only" aria-live="polite" role="status">
                {loadingText}
              </span>
            </div>
          </div>
        ) : (
          children
        )}
      </Comp>
    )
  }

  return (
    <Comp
      className={buttonClassName}
      ref={ref}
      disabled={props.disabled}
      tabIndex={props.tabIndex ?? (props.disabled ? -1 : undefined)}
      {...accessibilityProps}
      {...props}
    >
      {iconLeft ? <span className={elementOpacity}>{iconLeft}</span> : null}
      <span className={tw(textClassNameComputed, elementOpacity)}>{children}</span>
      {iconRight ? <span className={elementOpacity}>{iconRight}</span> : null}
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner variant="inverse" className={spinnerClassName} aria-hidden="true" />
          <span className="sr-only">{loadingText}</span>
        </div>
      ) : null}
    </Comp>
  )
}
Button.displayName = 'Button'

export { Button, buttonVariants }
